import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import MarketingLayout from "@/components/MarketingLayout";
import Seo from "@/components/Seo";
import api from "@/lib/api";
import { ArrowRight, CheckCircle, Sparkle } from "@phosphor-icons/react";

const REASSURE = ["No card required", "Every feature included", "Cancel anytime"];

export default function StartTrial() {
  const [params] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", practice: "", companies: "", plan_id: "", billing_cycle: "monthly", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => {
      setPlans(data.plans || []);
      const pre = params.get("plan");
      if (pre && (data.plans || []).some((p) => p.id === pre)) setForm((f) => ({ ...f, plan_id: pre }));
    }).catch(() => {});
  }, [params]);

  useEffect(() => {
    const ref = params.get("ref");
    if (ref && ref.startsWith("wp_") && !sessionStorage.getItem("visit_" + ref)) {
      sessionStorage.setItem("visit_" + ref, "1");
      api.post("/campaign/track-visit", { ref, ch: params.get("ch") || "" }).catch(() => {});
    }
  }, [params]);

  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/signup", { ...form, companies: form.companies ? Number(form.companies) : null, ref: params.get("ref") || "", ref_channel: params.get("ch") || "" });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setBusy(false);
      toast.error(typeof detail === "string" ? detail : "Something went wrong — please try again.");
    }
  };

  const inCls = "w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-navy placeholder:text-navy/30 focus:border-[#F37A24] focus:ring-2 focus:ring-[#F37A24]/20 outline-none transition-colors";

  return (
    <MarketingLayout>
      <Seo title="Start your free trial" path="/start"
        description="Start your 7-day free trial of WorkWithLottie. Connect Xero and automate accruals, prepayments and deferred income in minutes. Cancel any time before the trial ends." />
      <section className="max-w-5xl mx-auto px-6 pt-14 pb-24 grid lg:grid-cols-[1fr_1.1fr] gap-12 items-start">
        {/* Left: pitch */}
        <div className="pt-4">
          <div className="overline mb-4" style={{ color: "#F37A24" }}>Start free trial</div>
          <h1 className="font-heading font-black text-navy text-3xl sm:text-4xl tracking-tighter leading-tight">
            7 days of Lottie, on the house
          </h1>
          <p className="text-navy/70 mt-4 leading-relaxed">
            Tell us a little about your practice and we'll get your trial ready. Connect Xero,
            tag your nominal codes, and watch your accruals, prepayments and deferred income run themselves.
          </p>
          <ul className="mt-8 space-y-3">
            {REASSURE.map((r) => (
              <li key={r} className="flex items-center gap-2.5 text-navy/80" data-testid={`trial-reassure-${r.slice(0, 6)}`}>
                <CheckCircle size={20} weight="fill" className="text-positive shrink-0" /> {r}
              </li>
            ))}
          </ul>
          <p className="text-navy/65 text-sm mt-8">
            Already have an account? <Link to="/login" className="font-semibold text-navy hover:underline" data-testid="trial-signin-link">Sign in</Link>
          </p>
        </div>

        {/* Right: form / success */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-card p-7" data-testid="trial-form-card">
          {done ? (
            <div className="text-center py-8" data-testid="trial-success">
              <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center mb-5" style={{ background: "rgba(0,168,89,0.12)" }}>
                <Sparkle size={26} weight="fill" className="text-positive" />
              </div>
              <h2 className="font-heading font-bold text-navy text-2xl">Check your inbox!</h2>
              <p className="text-navy/70 mt-3 leading-relaxed">
                Thanks {form.name.split(" ")[0]} — your trial is ready. We've emailed <b className="text-navy">{form.email}</b> a
                secure link to set your password and sign straight in. It's valid for 24 hours.
              </p>
              <p className="text-navy/65 text-sm mt-3">
                Didn't get it? Check spam, or you can request a new link from the sign-in page.
              </p>
              <Link to="/login" data-testid="trial-success-signin"
                className="mt-7 inline-flex items-center gap-2 text-white font-semibold px-6 py-3 rounded-xl transition-transform hover:scale-[1.02]" style={{ background: "#F37A24" }}>
                Go to sign in <ArrowRight size={18} weight="bold" />
              </Link>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="trial-form">
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline text-navy/70 block mb-1.5">Your name *</span>
                  <input className={inCls} value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Jane Smith" required data-testid="trial-name" />
                </label>
                <label className="block">
                  <span className="overline text-navy/70 block mb-1.5">Work email *</span>
                  <input type="email" className={inCls} value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="jane@practice.co" required data-testid="trial-email" />
                </label>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <label className="block">
                  <span className="overline text-navy/70 block mb-1.5">Practice name</span>
                  <input className={inCls} value={form.practice} onChange={(e) => upd("practice", e.target.value)} placeholder="Smith & Co Accountants" data-testid="trial-practice" />
                </label>
                <label className="block">
                  <span className="overline text-navy/70 block mb-1.5">Companies you manage</span>
                  <input type="number" min={1} className={inCls} value={form.companies} onChange={(e) => upd("companies", e.target.value)} placeholder="e.g. 12" data-testid="trial-companies" />
                </label>
              </div>
              <label className="block">
                <span className="overline text-navy/70 block mb-1.5">Plan you're interested in</span>
                <select className={inCls} value={form.plan_id} onChange={(e) => upd("plan_id", e.target.value)} data-testid="trial-plan">
                  <option value="">Not sure yet — help me choose</option>
                  {plans.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}{p.enterprise ? " (51+ companies)" : p.companies === 1 ? " (1 company)" : ` (up to ${p.companies})`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="overline text-navy/70 block mb-1.5">Anything we should know? (optional)</span>
                <textarea rows={3} className={inCls} value={form.message} onChange={(e) => upd("message", e.target.value)} placeholder="Tell us about your month-end process…" data-testid="trial-message" />
              </label>
              <button type="submit" disabled={busy} data-testid="trial-submit"
                className="w-full inline-flex items-center justify-center gap-2 text-white font-semibold py-3 rounded-xl transition-transform hover:scale-[1.01] disabled:opacity-60"
                style={{ background: "#F37A24" }}>
                {busy ? "SENDING…" : "Start my free trial"} {!busy && <ArrowRight size={18} weight="bold" />}
              </button>
              <p className="text-center text-navy/65 text-xs">7-day free trial · No card required · Cancel anytime</p>
            </form>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
